import classNames from 'classnames';
import React from 'react';
import * as styles from './Stats.module.scss';
import SVGIcon from '../components/SVGIcon';
import type { AlphaHistoryStats } from '../graphql/generated/graphql';
import checkIcon from '../icons/non-prefilled/16x16/check.svg';

interface StatGroupProps {
  label: string;
  children: React.ReactNode;
}

const StatGroup = ({ label, children }: StatGroupProps) => (
  <span>
    <span>{label}</span>
    {children}
  </span>
);

interface StatProps {
  icon: React.ReactNode;
  children: number;
  variant: 'success' | 'danger' | 'warning' | 'muted';
}

const Stat = ({ icon, children, variant }: StatProps) => {
  if (children === 0) {
    return null;
  }

  return (
    <span className={classNames(styles.stat, [styles[variant]])}>
      <span>{icon}</span>
      <span>{children}</span>
    </span>
  );
};

type HistoryStats = Omit<AlphaHistoryStats, 'format'>;

const DEFAULT_MAX_VALUES: HistoryStats = {
  insert: 999,
  delete: 999,
  comment: 99,
  resolve: 99,
};

interface StatsProps {
  stats: HistoryStats;
  maxValues?: Partial<HistoryStats>;
}

const Stats = ({ stats, maxValues = DEFAULT_MAX_VALUES }: StatsProps) => {
  return (
    <span className={styles.container}>
      {(!!stats.insert || !!stats.delete) && (
        <StatGroup label="Edits">
          <Stat
            variant="success"
            icon={<span className={styles.plusMinusSign}>+</span>}
          >
            {Math.min(maxValues.insert ?? Infinity, stats.insert)}
          </Stat>
          <Stat
            variant="danger"
            icon={<span className={styles.plusMinusSign}>-</span>}
          >
            {Math.min(maxValues.delete ?? Infinity, stats.delete)}
          </Stat>
        </StatGroup>
      )}

      {(!!stats.comment || !!stats.resolve) && (
        <StatGroup label="Comments">
          <Stat
            variant="warning"
            icon={<span className={styles.plusMinusSign}>+</span>}
          >
            {Math.min(maxValues.comment ?? Infinity, stats.comment)}
          </Stat>
          <Stat
            variant="muted"
            icon={
              <SVGIcon inline size={8} className={styles.checkIcon}>
                {checkIcon}
              </SVGIcon>
            }
          >
            {Math.min(maxValues.resolve ?? Infinity, stats.resolve)}
          </Stat>
        </StatGroup>
      )}
    </span>
  );
};

export default Stats;
